import { useEffect, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import AppRoutes from "./routes";
import { supabase } from "@/integrations/supabase/client";

const queryClient = new QueryClient();

const App = () => {
  const [setupCompleted, setSetupCompleted] = useState<boolean | null>(null);

  useEffect(() => {
    const checkSetupStatus = async () => {
      try {
        // Check if there's a superadmin user in the database (role ID 1)
        const { data: superAdminExists, error } = await supabase
          .from("user_to_roles")
          .select("id")
          .eq("role_id", 1)
          .limit(1);

        if (error) {
          console.error("Error checking superadmin status:", error);
          // If there's an error querying the database, fall back to localStorage
          const isSetupComplete =
            localStorage.getItem("initial_setup_complete") === "true";
          setSetupCompleted(isSetupComplete);
          return;
        }

        // Setup is complete if there's at least one superadmin user
        const hasSuperAdmin = superAdminExists && superAdminExists.length > 0;
        setSetupCompleted(hasSuperAdmin);

        // Update localStorage to match the database state
        if (hasSuperAdmin) {
          localStorage.setItem("initial_setup_complete", "true");
        } else {
          localStorage.removeItem("initial_setup_complete");
        }
      } catch (error) {
        console.error("Unexpected error checking setup status:", error);
        // Fall back to localStorage on unexpected errors
        const isSetupComplete =
          localStorage.getItem("initial_setup_complete") === "true";
        setSetupCompleted(isSetupComplete);
      }
    };

    checkSetupStatus();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <TooltipProvider>
          <AuthProvider>
            <AppRoutes setupCompleted={setupCompleted} />
            <Toaster />
            <Sonner />
          </AuthProvider>
        </TooltipProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;
